import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { sanitizeHtml } from "@/lib/sanitize";
import {
  ArrowLeft,
  Edit,
  Trash2,
  ExternalLink,
  Upload,
  RefreshCw,
  History,
  Bot,
  FileText,
  BarChart3,
  Loader2,
  CheckCircle,
  Clock,
  DollarSign,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { ArticleListSkeleton } from "@/components/ui/skeleton";
import { InlineError } from "@/components/ErrorBoundary";
import { GenerationProgress } from "@/components/articles/GenerationProgress";
import { articlesApi } from "@/services/api";
import type { ArticleDetail } from "@/types";

// v2 pipeline status configuration
const statusConfig: Record<string, { label: string; color: string }> = {
  draft: { label: "Draft", color: "bg-gray-100 text-gray-700" },
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-700" },
  // v2 pipeline statuses
  planning: { label: "Planning", color: "bg-blue-100 text-blue-700" },
  writing: { label: "Writing", color: "bg-blue-100 text-blue-700" },
  optimizing: { label: "Optimizing", color: "bg-purple-100 text-purple-700" },
  finalizing: { label: "Finalizing", color: "bg-purple-100 text-purple-700" },
  generating_image: {
    label: "Generating Image",
    color: "bg-indigo-100 text-indigo-700",
  },
  ready: { label: "Ready", color: "bg-green-100 text-green-700" },
  published: { label: "Published", color: "bg-emerald-100 text-emerald-700" },
  failed: { label: "Failed", color: "bg-red-100 text-red-700" },
  // Legacy v1 statuses (backwards compatibility)
  researching: { label: "Researching", color: "bg-blue-100 text-blue-700" },
  editing: { label: "Editing", color: "bg-purple-100 text-purple-700" },
  humanizing: { label: "Humanizing", color: "bg-purple-100 text-purple-700" },
};

const isGenerating = (status: string) =>
  [
    "pending",
    "planning",
    "writing",
    "optimizing",
    "finalizing",
    "generating_image",
    "researching",
    "editing",
    "humanizing",
  ].includes(status);

export function ArticleDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("content");

  const {
    data: article,
    isLoading,
    error,
    refetch,
  } = useQuery<ArticleDetail>({
    queryKey: ["article", id],
    queryFn: () => articlesApi.get(id!),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => articlesApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["articles"] });
      navigate("/articles");
    },
  });

  const retryMutation = useMutation({
    mutationFn: () => articlesApi.retry(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["article", id] });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/articles">
              <ArrowLeft className="h-4 w-4 me-2" />
              {t("articleDetail.back")}
            </Link>
          </Button>
        </div>
        <ArticleListSkeleton count={1} />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/articles">
              <ArrowLeft className="h-4 w-4 me-2" />
              {t("articleDetail.back")}
            </Link>
          </Button>
        </div>
        <InlineError
          error={(error as Error) || "Article not found"}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  const status = statusConfig[article.status] || {
    label: article.status,
    color: "bg-gray-100 text-gray-700",
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/articles">
              <ArrowLeft className="h-4 w-4 me-2" />
              {t("articleDetail.back")}
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{article.title}</h1>
            <div className="flex items-center gap-3 mt-2">
              <Badge className={status.color}>{status.label}</Badge>
              {article.word_count > 0 && (
                <span className="text-sm text-muted-foreground">
                  {article.word_count} {t("articleDetail.words")}
                </span>
              )}
              <span className="text-sm text-muted-foreground">
                {t("articleDetail.created")}{" "}
                {new Date(article.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {article.status === "failed" && (
            <Button
              variant="outline"
              onClick={() => retryMutation.mutate()}
              disabled={retryMutation.isPending}
            >
              {retryMutation.isPending ? (
                <Loader2 className="h-4 w-4 me-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 me-2" />
              )}
              {t("articleDetail.retry")}
            </Button>
          )}
          {article.wp_post_url && (
            <Button variant="outline" asChild>
              <a
                href={article.wp_post_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-4 w-4 me-2" />
                {t("articleDetail.viewPost")}
              </a>
            </Button>
          )}
          {article.status === "ready" && (
            <Button variant="outline">
              <Upload className="h-4 w-4 me-2" />
              {t("articleDetail.publish")}
            </Button>
          )}
          <Button variant="outline" asChild>
            <Link to={`/articles/${id}/edit`}>
              <Edit className="h-4 w-4 me-2" />
              {t("articleDetail.edit")}
            </Link>
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              if (
                confirm(
                  t("app.confirmDelete") ||
                    "Are you sure you want to delete this article?",
                )
              ) {
                deleteMutation.mutate();
              }
            }}
            disabled={deleteMutation.isPending}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      {/* Generation Progress (if generating) */}
      {isGenerating(article.status) && (
        <GenerationProgress
          articleId={article.id}
          initialStatus={article.status}
          initialProgress={article.progress}
          onComplete={() => refetch()}
        />
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-blue-600" />
              {t("articleDetail.stats.seoScore")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{article.seo_score ?? "-"}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-purple-600" />
              {t("articleDetail.stats.geoScore")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{article.geo_score ?? "-"}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              {t("articleDetail.stats.humanScore")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {article.human_score ?? "-"}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-yellow-600" />
              {t("articleDetail.stats.generationCost")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${(article.generation_cost || 0).toFixed(2)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-600" />
              {t("articleDetail.stats.generationTime")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {article.generation_time != null
                ? article.generation_time >= 60
                  ? `${Math.floor(article.generation_time / 60)}:${String(Math.floor(article.generation_time % 60)).padStart(2, "0")}`
                  : `${Math.floor(article.generation_time)}s`
                : "-"}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="content" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            {t("articleDetail.tabs.content")}
          </TabsTrigger>
          <TabsTrigger value="meta" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            {t("articleDetail.tabs.seoMeta")}
          </TabsTrigger>
          <TabsTrigger value="agents" className="flex items-center gap-2">
            <Bot className="h-4 w-4" />
            {t("articleDetail.tabs.agentLogs")}
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            {t("articleDetail.tabs.history")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("articleDetail.content.title")}</CardTitle>
              <CardDescription>
                {t("articleDetail.content.description")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {article.html_content ? (
                <div
                  className="prose dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(article.html_content),
                  }}
                />
              ) : (
                <div className="whitespace-pre-wrap text-sm">
                  {article.content}
                </div>
              )}

              {article.tags.length > 0 && (
                <>
                  <Separator className="my-6" />
                  <div>
                    <h4 className="font-medium mb-2">
                      {t("articleDetail.content.tags")}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {article.tags.map((tag) => (
                        <Badge key={tag} variant="secondary">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="meta" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("articleDetail.meta.title")}</CardTitle>
              <CardDescription>
                {t("articleDetail.meta.description")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-medium mb-2">
                  {t("articleDetail.meta.metaTitle")}
                </h4>
                <p className="text-sm bg-muted p-3 rounded-md">
                  {article.meta_title || (
                    <span className="text-muted-foreground italic">
                      {t("articleDetail.meta.notSet")}
                    </span>
                  )}
                </p>
                {article.meta_title && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {article.meta_title.length}/60{" "}
                    {t("articleDetail.meta.characters")}
                  </p>
                )}
              </div>

              <div>
                <h4 className="font-medium mb-2">
                  {t("articleDetail.meta.metaDescription")}
                </h4>
                <p className="text-sm bg-muted p-3 rounded-md">
                  {article.meta_description || (
                    <span className="text-muted-foreground italic">
                      {t("articleDetail.meta.notSet")}
                    </span>
                  )}
                </p>
                {article.meta_description && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {article.meta_description.length}/160{" "}
                    {t("articleDetail.meta.characters")}
                  </p>
                )}
              </div>

              <div>
                <h4 className="font-medium mb-2">
                  {t("articleDetail.meta.urlSlug")}
                </h4>
                <p className="text-sm bg-muted p-3 rounded-md font-mono">
                  {article.slug || (
                    <span className="text-muted-foreground italic">
                      {t("articleDetail.meta.notSet")}
                    </span>
                  )}
                </p>
              </div>

              {article.image_url && (
                <div>
                  <h4 className="font-medium mb-2">
                    {t("articleDetail.meta.featuredImage")}
                  </h4>
                  <img
                    src={article.image_url}
                    alt="Featured"
                    className="rounded-md max-h-64 object-cover"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="agents" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("articleDetail.agents.title")}</CardTitle>
              <CardDescription>
                {t("articleDetail.agents.description")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {article.agent_results &&
              Object.keys(article.agent_results).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(article.agent_results).map(
                    ([agent, result]) => (
                      <div key={agent} className="border rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Bot className="h-4 w-4 text-blue-600" />
                          <h4 className="font-medium capitalize">
                            {agent} {t("articleDetail.agents.agent")}
                          </h4>
                        </div>
                        <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-40">
                          {JSON.stringify(result, null, 2)}
                        </pre>
                      </div>
                    ),
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  {t("articleDetail.agents.noLogs")}
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("articleDetail.history.title")}</CardTitle>
              <CardDescription>
                {t("articleDetail.history.description")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Version timeline */}
                <div className="relative ps-6 border-s-2 border-muted space-y-6">
                  <div className="relative">
                    <div className="absolute -start-[25px] h-4 w-4 rounded-full bg-green-500" />
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">
                          {t("articleDetail.history.currentVersion")}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {t("articleDetail.history.lastUpdated")}{" "}
                          {new Date(article.updated_at).toLocaleString()}
                        </p>
                      </div>
                      <Badge variant="outline">
                        {t("articleDetail.history.active")}
                      </Badge>
                    </div>
                  </div>

                  <div className="relative">
                    <div className="absolute -start-[25px] h-4 w-4 rounded-full bg-muted" />
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">
                          {t("articleDetail.history.originalGeneration")}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {t("articleDetail.history.created")}{" "}
                          {new Date(article.created_at).toLocaleString()}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {t("articleDetail.history.initial")}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground text-center pt-4">
                  {t("articleDetail.history.comingSoon")}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
